import { useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { ArrowLeft } from 'lucide-react';
import { useEmployee, useUpdateEmployee } from '@/api/queries/employee.queries';
import { PageHeader } from '@/components/shared/PageHeader';
import { Button } from '@/components/ui/button';
import { EmployeeForm } from '@/components/employees/EmployeeForm';

export default function EditEmployeePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data: empResp, isLoading } = useEmployee(id);
  const updateEmployee = useUpdateEmployee(id);
  const [serverError, setServerError] = useState('');
  const [saved, setSaved] = useState(false);

  const emp = empResp?.data;

  const handleSubmit = async (payload) => {
    setServerError('');
    try {
      await updateEmployee.mutateAsync(payload);
      setSaved(true);
      setTimeout(() => navigate(`/employees/${id}`), 800);
    } catch (err) {
      setServerError(err?.response?.data?.error?.message || 'Failed to update employee.');
    }
  };

  if (isLoading) return <div className="card text-center py-16 text-[14px] text-[var(--text-tertiary)]">Loading…</div>;
  if (!emp) return (
    <div className="card text-center py-16">
      <p className="text-[14px] text-[var(--text-secondary)]">Employee not found</p>
      <Button variant="ghost" onClick={() => navigate('/employees')} className="mt-3">Back</Button>
    </div>
  );

  return (
    <div className="max-w-5xl space-y-5 fade-in">
      <PageHeader
        title={`Edit — ${emp.first_name} ${emp.last_name}`}
        subtitle={emp.employee_code}
        breadcrumb={[
          { label: 'Employees', href: '/employees' },
          { label: `${emp.first_name} ${emp.last_name}`, href: `/employees/${id}` },
          { label: 'Edit' },
        ]}
        actions={
          <button onClick={() => navigate(`/employees/${id}`)}
            className="flex items-center gap-1.5 text-[13px] text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors">
            <ArrowLeft size={15}/> Back
          </button>
        }
      />

      {saved && (
        <div className="px-4 py-3 rounded-[10px] bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-[13px] text-green-700 dark:text-green-400 font-medium">
          ✓ Employee updated successfully — redirecting…
        </div>
      )}

      <EmployeeForm initialData={emp} onSubmit={handleSubmit} submitting={updateEmployee.isPending} serverError={serverError}/>
    </div>
  );
}
